import {
  SUBSCRIPTION_LOCKOUT_RECOVERY_MESSAGE,
  isSubscriptionWriteLockError,
} from './subscription-lockout-error';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export type FieldErrors = Record<string, string[]>;

interface ErrorWithData {
  data?: unknown;
  shape?: { data?: unknown };
}

function readMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return '';
}

/**
 * The one way to turn a caught error (TRPCClientError, Error, or unknown) into toast/JSX copy.
 * The API formatter already makes tRPC messages readable; anything that still looks like
 * serialized JSON falls back so raw payloads never reach users.
 */
export function getUserErrorMessage(
  error: unknown,
  fallback: string = DEFAULT_ERROR_MESSAGE
): string {
  if (isSubscriptionWriteLockError(error)) return SUBSCRIPTION_LOCKOUT_RECOVERY_MESSAGE;

  const message = readMessage(error).trim();
  if (!message || message.startsWith('{') || message.startsWith('[')) return fallback;
  return message;
}

function readFieldErrors(data: unknown): FieldErrors | null {
  if (typeof data !== 'object' || data === null) return null;
  const fieldErrors = (data as { fieldErrors?: unknown }).fieldErrors;
  return typeof fieldErrors === 'object' && fieldErrors !== null
    ? (fieldErrors as FieldErrors)
    : null;
}

/** Per-field validation messages keyed by dotted input path (e.g. `name`, `lineItems.0.unitPrice`). */
export function getFieldErrors(error: unknown): FieldErrors {
  if (typeof error !== 'object' || error === null) return {};
  const { data, shape } = error as ErrorWithData;
  return readFieldErrors(data) ?? readFieldErrors(shape?.data) ?? {};
}
